import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ClientContractRevisionResponse } from '@interfaces/companys/contracts/responses/client-contract-revision.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { findVariableKeys } from '@src/app/shared/components/rich-text-editor/rich-text-editor.commands';
import { RichTextEditorComponent } from '@src/app/shared/components/rich-text-editor/rich-text-editor.component';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Observable, first } from 'rxjs';
import { PartnerContractUnsavedChanges } from '../../../../partner/guards/partner-contract-unsaved-changes.guard';
import { ModalPartnerContractPreviewComponent } from '../../../../partner/pages/partner-contract-editor/components/modal-partner-contract-preview/modal-partner-contract-preview.component';
import { PartnerContractRevisionHistoryComponent } from '../../../../partner/pages/partner-contract-editor/components/partner-contract-revision-history/partner-contract-revision-history.component';
import {
  backendErrorMessage,
  describeUnfilledVariables,
} from '../../../../partner/utils/partner-contract.utils';
import { ClientContractAutomaticDataComponent } from '../../components/client-contract-automatic-data/client-contract-automatic-data.component';
import { ClientContractFieldsFormComponent } from '../../components/client-contract-fields-form/client-contract-fields-form.component';
import {
  clientContractRevisionActionLabel,
  clientContractStatusLabel,
  clientContractTypeLabel,
  clientContractValueFormatter,
  describeClientOutOfDefaultFields,
} from '../../utils/client-contract.utils';
import { ClientContractEditorState } from './client-contract-editor.state';

/**
 * Editor da minuta de um contrato de cliente, Flat Fee ou Gestão Ativa (card 3643). Rota
 * `company/contracts/:id` (`CLIENTCONTRACT_VIEW`).
 *
 * Mesmo desenho do editor do contrato de parceria: barra compacta (voltar para a empresa, título,
 * situação, número, tipo; ícones Histórico, Pré-visualizar, Baixar PDF; "⋯" com Excluir; Salvar e
 * Finalizar/Reabrir), a prévia do texto renderizado no centro e o painel "Campos do contrato" (campos
 * do catálogo, dados do cadastro só leitura e blocos do GA). Só quem está na lista de editores do
 * texto vê "Editar texto". Reaproveita a pré-visualização, o histórico e o guard da parceria.
 */
@Component({
  selector: 'app-client-contract-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    VaultDateTimePipe,
    RichTextEditorComponent,
    ClientContractFieldsFormComponent,
    ClientContractAutomaticDataComponent,
    ModalPartnerContractPreviewComponent,
    PartnerContractRevisionHistoryComponent,
  ],
  templateUrl: './client-contract-editor.component.html',
  styleUrls: ['./client-contract-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClientContractEditorState],
})
export class ClientContractEditorComponent implements OnInit, PartnerContractUnsavedChanges {
  readonly state = inject(ClientContractEditorState);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('previewModal') private previewModal?: ModalPartnerContractPreviewComponent;

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly canUpdate = this.permissionSignal(Permissions.ClientContract.UPDATE);
  readonly canFinalize = this.permissionSignal(Permissions.ClientContract.FINALIZE);
  readonly canDelete = this.permissionSignal(Permissions.ClientContract.DELETE);
  readonly canExportPdf = this.permissionSignal(Permissions.ClientContract.EXPORT_PDF);

  /** Painel lateral: campos do contrato, histórico ou recolhido. */
  readonly asidePanel = signal<'fields' | 'history' | null>('fields');
  readonly selectedRevisionId = signal<string | null>(null);
  readonly editingTitle = signal(false);
  readonly typeLabel = clientContractTypeLabel;
  readonly revisionActionLabel = clientContractRevisionActionLabel;
  /** Exemplo exibido no aviso do texto-fonte (no template, `{{` seria lido como interpolação). */
  readonly variableExample = '{{chave}}';

  /** Revisão aberta na pré-visualização ("Usar este texto"). */
  private viewedRevision: ClientContractRevisionResponse | null = null;

  readonly moreMenuItems: MenuItem[] = [
    { label: 'Excluir minuta', icon: 'pi pi-trash', command: () => this.confirmDelete() },
  ];

  /** Minuta travada: finalizada ou sem permissão de editar. */
  readonly locked = computed(() => this.state.isFinalized() || !this.canUpdate());
  readonly canEditTextNow = computed(() => !this.locked() && this.state.canEditText());
  readonly statusLabel = computed(() => clientContractStatusLabel(this.state.contract()?.status));
  /** Histórico: R$ ou % conforme o tipo do campo no catálogo. */
  readonly valueFormatter = computed(() =>
    clientContractValueFormatter(this.state.catalog().fields)
  );
  readonly outOfDefaultDescription = computed(() => {
    const contract = this.state.contract();
    return contract?.requiresApproval
      ? describeClientOutOfDefaultFields(contract.outOfDefaultFields, this.state.catalog().fields)
      : '';
  });
  readonly unfilledDescription = computed(() =>
    describeUnfilledVariables(this.state.unfilledKeys(), this.state.variables())
  );

  readonly breadcrumbs = computed<MenuItem[]>(() => {
    const contract = this.state.contract();
    return [
      { label: 'Gerenciamento' },
      { label: 'Empresas', routerLink: '/dashboard/company/list' },
      {
        label: contract?.companyName || 'Empresa',
        routerLink: contract ? `/dashboard/company/register/${contract.companyId}` : undefined,
      },
      { label: contract?.contractNumber || 'Contrato' },
    ];
  });

  constructor() {
    effect(() => {
      const options = { emitEvent: false };
      if (this.locked()) {
        this.state.titleControl.disable(options);
        this.state.fieldsForm.root.disable(options);
      } else {
        this.state.titleControl.enable(options);
        this.state.fieldsForm.root.enable(options);
      }
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/dashboard/company/list']);
      return;
    }

    this.state
      .load(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: contract => {
          if (!contract) {
            this.toast.showToastError('Contrato não encontrado', 'Ele pode ter sido excluído.');
            this.router.navigate(['/dashboard/company/list']);
          }
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível abrir o contrato',
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  canSave(): boolean {
    return !this.locked() && this.state.dirty() && !this.state.busy();
  }

  save(): void {
    if (!this.canSave() || !this.ensureValid()) return;

    this.state
      .save()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () =>
          this.toast.showToastSuccess('Contrato salvo', 'O texto já está com os valores.'),
        error: error => this.showError('Não foi possível salvar o contrato', error),
      });
  }

  /** Pré-visualiza o texto renderizado gravado; com "Editar texto", o corpo-fonte em edição. */
  openPreview(): void {
    const editingSource = this.state.textMode();
    const bodyHtml = editingSource
      ? this.state.sourceBodyControl.value
      : this.state.renderedBodyControl.value;
    this.previewModal?.open({
      title: this.state.titleControl.value,
      bodyHtml,
      unfilledKeys: editingSource ? findVariableKeys(bodyHtml ?? '') : this.state.unfilledKeys(),
      variables: this.state.variables(),
    });
  }

  toggleTextMode(): void {
    if (!this.canEditTextNow()) return;
    this.state.toggleTextMode();
  }

  /** Baixa o PDF. Com mudança não salva (e permissão de editar), salva antes. */
  downloadPdf(): void {
    if (this.state.busy()) return;

    if (this.state.dirty() && !this.locked()) {
      if (!this.ensureValid()) return;
      this.state
        .save()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.runDownload(),
          error: error => this.showError('Não foi possível salvar antes de gerar o PDF', error),
        });
      return;
    }

    this.runDownload();
  }

  confirmFinalize(): void {
    if (this.state.busy() || !this.ensureValid()) return;
    const contract = this.state.contract();
    const pending = [
      ...this.missingRequiredFields(),
      ...(contract?.missingAutomatic ?? []),
      ...(this.unfilledDescription() ? [this.unfilledDescription()] : []),
    ].join(', ');

    this.confirmationService
      .confirm(
        'Finalizar o contrato?',
        (pending ? `Ainda faltam dados: ${pending}. ` : '') +
          'O texto fica travado e os dados do cadastro da empresa ficam congelados; para mudar depois, use Reabrir.',
        pending ? 'danger' : 'default',
        'Cancelar',
        'Finalizar'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.state
          .finalize()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () =>
              this.toast.showToastSuccess(
                'Contrato finalizado',
                'O texto está travado para edição.'
              ),
            error: error => this.showError('Não foi possível finalizar o contrato', error),
          });
      });
  }

  reopen(): void {
    if (this.state.busy()) return;
    this.state
      .reopen()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () =>
          this.toast.showToastSuccess(
            'Contrato reaberto',
            'Ele voltou para rascunho; os dados do cadastro voltam a ser lidos da empresa.'
          ),
        error: error => this.showError('Não foi possível reabrir o contrato', error),
      });
  }

  confirmDelete(): void {
    const contract = this.state.contract();
    if (!contract || this.state.busy()) return;

    this.confirmationService
      .confirm(
        `Excluir o contrato ${contract.contractNumber}?`,
        'A minuta sai da lista da empresa. O modelo e os outros contratos não mudam.',
        'danger',
        'Cancelar',
        'Excluir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.state
          .delete()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toast.showToastSuccess('Contrato excluído', '');
              this.state.dirty.set(false);
              this.backToCompany();
            },
            error: error => this.showError('Não foi possível excluir o contrato', error),
          });
      });
  }

  backToCompany(): void {
    const companyId = this.state.contract()?.companyId;
    if (companyId) {
      this.router.navigate(['/dashboard/company/register', companyId]);
    } else {
      this.router.navigate(['/dashboard/company/list']);
    }
  }

  toggleFieldsPanel(): void {
    this.asidePanel.update(panel => (panel === 'fields' ? null : 'fields'));
  }

  /** Abre o histórico no lugar dos campos (e busca a lista); de novo, volta aos campos. */
  toggleHistory(): void {
    if (this.asidePanel() === 'history') {
      this.asidePanel.set('fields');
      return;
    }
    this.asidePanel.set('history');
    this.state.requestRevisions();
  }

  /** Abre a pré-visualização com o texto da revisão escolhida ("Usar este texto" só para editor, com corpo-fonte). */
  openRevision(item: { id: string; revisionNumber: number }): void {
    this.state
      .getRevision(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: revision => {
          if (!revision) return;
          this.viewedRevision = revision;
          this.selectedRevisionId.set(revision.id);
          const author = revision.createdByName ? ` · ${revision.createdByName}` : '';
          this.previewModal?.open({
            title: revision.title,
            bodyHtml: revision.bodyHtml,
            unfilledKeys: findVariableKeys(revision.bodyHtml ?? ''),
            variables: this.state.variables(),
            revision: {
              label: `Revisão ${revision.revisionNumber} · ${clientContractRevisionActionLabel(revision.action)}${author}`,
              // Só com o corpo-fonte da revisão (vem só para editor do texto): restaura tokens e blocos.
              canUseText: this.canEditTextNow() && !!revision.sourceBodyHtml,
            },
          });
        },
        error: error =>
          this.showError(`Não foi possível abrir a revisão ${item.revisionNumber}`, error),
      });
  }

  /** "Usar este texto" da pré-visualização: leva título e corpo-fonte da revisão ao editor, sem salvar. */
  useRevisionText(): void {
    const revision = this.viewedRevision;
    if (!revision || !this.canEditTextNow() || !revision.sourceBodyHtml) return;

    const apply = () => {
      this.state.useRevisionText(revision);
      this.toast.showToastInfo(
        `Texto da revisão ${revision.revisionNumber} no editor`,
        'O texto volta com as variáveis e os blocos, preenchidos pelos valores atuais ao salvar. Revise e use Salvar para gravar.'
      );
    };

    if (!this.state.dirty()) {
      apply();
      return;
    }
    this.confirmationService
      .confirm(
        'Substituir o texto do editor?',
        'As mudanças ainda não salvas no título e no texto serão trocadas pelo texto da revisão.',
        'danger',
        'Cancelar',
        'Substituir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) apply();
      });
  }

  /** "Corrigir no cadastro": abre o cadastro da empresa (o guard pergunta se houver mudança). */
  openCompanyRegistration(): void {
    const companyId = this.state.contract()?.companyId;
    if (companyId) this.router.navigate(['/dashboard/company/register', companyId]);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.save();
    }
  }

  confirmLeave(): boolean | Observable<boolean> {
    if (!this.state.dirty()) return true;
    return this.confirmationService
      .confirm(
        'Sair sem salvar?',
        'As mudanças neste contrato ainda não foram salvas e serão perdidas.',
        'danger',
        'Continuar editando',
        'Sair sem salvar'
      )
      .pipe(first());
  }

  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.state.dirty()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Campos obrigatórios para finalizar que estão vazios (pelo rótulo do catálogo). */
  private missingRequiredFields(): string[] {
    return this.state.fieldsForm.entries
      .filter(entry => entry.field.required && (entry.control.value ?? null) === null)
      .map(entry => entry.field.label);
  }

  private runDownload(): void {
    this.state
      .downloadPdf()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: error => this.showError('Não foi possível gerar o PDF', error) });
  }

  /** Título obrigatório e valores dentro dos limites; diz onde corrigir. */
  private ensureValid(): boolean {
    if (this.state.isValid()) return true;
    this.state.fieldsForm.root.markAllAsTouched();
    this.state.titleControl.markAsTouched();
    if (this.state.titleControl.invalid || !this.state.titleControl.value.trim()) {
      this.editingTitle.set(true);
    }
    this.asidePanel.set('fields');
    const invalid = this.state.fieldsForm.invalidLabels();
    this.toast.showToastWarn(
      'Faltam dados para salvar',
      invalid.length
        ? `Corrija no painel "Campos do contrato": ${invalid.join(', ')} (fora dos limites).`
        : 'Preencha o título do contrato (lápis ao lado do nome).'
    );
    return false;
  }

  private showError(title: string, error: unknown): void {
    this.toast.showToastError(title, backendErrorMessage(error) || 'Tente de novo em instantes.');
  }

  private permissionSignal(permission: string) {
    return toSignal(this.permissionsService.hasPermission(permission), { initialValue: false });
  }
}
